import React, { useState, useMemo, useRef, useEffect } from 'react';
import zipcelx from 'zipcelx';
import { cloneDeep } from 'lodash';
import Highcharts, { Options as HighchartsOptions } from 'highcharts';
import '~/shared/utils/highchartsAllowList';
import HCAccessibility from 'highcharts/modules/accessibility';
import HighchartsReact from 'highcharts-react-official';
import HCExporting from 'highcharts/modules/exporting';
import HCOfflineExporting from 'highcharts/modules/offline-exporting';
import HCExportData from 'highcharts/modules/export-data';
import HCNoData from 'highcharts/modules/no-data-to-display';
import Humanize from 'humanize-plus';
import styled from 'styled-components';
import reactGA from 'react-ga';
import CustomEvents from 'highcharts-custom-events';
import moment from 'moment';
import { highchartsTooltipCfg } from '~/shared/config/highchartsCfg';
import { hex2rgba } from '../../../utils/colors/hex2rgba';
import { formatTooltipHtml } from '~/shared/utils';
import { buildChartExportFileName } from '~/shared/utils/buildChartExportFileName';
import { Loader } from '~/shared/components';
import { useContainerReady } from '~/shared/hooks';

HCExporting(Highcharts);
HCExportData(Highcharts);
HCNoData(Highcharts);
HCOfflineExporting(Highcharts);
CustomEvents(Highcharts as any);
HCAccessibility(Highcharts);

const EMPTY_OPTIONS: HighchartsOptions = { series: [] };

(function (H) {
  H.Chart.prototype.downloadXLS = function () {
    const div = document.createElement('div');
    let xlsxRows = [];
    div.style.display = 'none';
    document.body.appendChild(div);
    const rows = this.getDataRows(true);
    xlsxRows = H.map(rows.slice(1), function (row: any) {
      return H.map(row, function (column: any) {
        let tempColumn = column;
        if (typeof column === 'string' && column?.includes('00:00:00')) {
          tempColumn = moment(column).format('MM/DD/YYYY');
        }
        return {
          type: typeof column === 'number' ? 'number' : 'string',
          value: tempColumn,
        };
      });
    });

    const name = this.options.exporting!.filename;
    zipcelx({
      filename: name as unknown as string,
      sheet: {
        data: xlsxRows,
      },
    });
  };
})(Highcharts);

export interface IStackBarChart {
  axisXLabels: string[];
  axisYLabels: string[];
  values: { XLabel: string; YLabel: string; Value: number }[];
  matrix: number[][];
  data?: any;
}

interface ISeriesObj {
  name: string;
  data: string[];
  color: string | undefined;
}

const Root = styled.div<{ $gridheight: number }>`
  height: ${({ $gridheight }) => ($gridheight / 4) * 470 + ($gridheight / 4 - 1) * 23}px;
  width: 100%;
  position: relative;
  margin-top: 10px;
  border-bottom: 1px solid black;
`;

const HighchartContainer = styled.div<{ $isnegative: number }>`
  height: 100%;
  width: 100%;
  box-sizing: border-box;
  position: absolute;
  border: solid 1px var(--border-chart);
  background: var(--background-chart);
  padding: 10px;
  .highcharts-series-0 > .highcharts-point {
    transform: translate(0px, 0px) !important;
  }
  .highcharts-series-1 > .highcharts-point {
    transform: translate(0px, ${({ $isnegative }) => ($isnegative ? '0' : '22')}px) !important;
  }
  .highcharts-legend-item > .highcharts-point {
    transform: unset !important;
  }
  .highcharts-xaxis {
    display: none;
  }
  .highcharts-button-box {
    x: 2;
    y: 1;
    rx: 16;
    ry: 16;
    &:hover {
      fill: #d4e5ff !important;
    }
  }
  .highcharts-button-symbol {
    width: 3.5px !important;
    fill: #4c5e72 !important;
    stroke: transparent !important;
    pointer-events: none;
  }
  .highcharts-indicator-menu {
    max-width: 460px;
    right: 0px !important;
    left: unset !important;
  }
`;

const LoaderWrapper = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--background-chart);
  z-index: 2;
`;

Highcharts.SVGRenderer.prototype.symbols.ellipsisvertical = function () {
  const path = [
    'M18.1538 16C18.1538 17.1451 17.222 18.0769 16.0769 18.0769C14.9318 18.0769 14 17.1451 14 16C14 14.8549 14.9318 13.9231 16.0769 13.9231C17.222 13.9231 18.1538 14.8549 18.1538 16Z M18.1538 22.9231C18.1538 24.0682 17.222 25 16.0769 25C14.9318 25 14 24.0682 14 22.9231C14 21.778 14.9318 20.8462 16.0769 20.8462C17.222 20.8462 18.1538 21.778 18.1538 22.9231Z M18.1538 9.07692C18.1538 10.222 17.222 11.1538 16.0769 11.1538C14.9318 11.1538 14 10.222 14 9.07692C14 7.93185 14.9318 7 16.0769 7C17.222 7 18.1538 7.93185 18.1538 9.07692Z',
  ];
  return path;
};

export default function HorizontalStackedBarChart({
  data,
  size,
  onClick,
  prism,
  filterValues,
  colorScheme,
  chartTitle,
  chartSubtitle,
  chartYtitle,
  tooltipCfg,
  gridHeight = 4,
  gridWidth,
  gridIndex,
  gridPrism,
  chartYmax,
  chartYmin,
}: any) {
  const chartRef = useRef<any>(null);
  const lastPositiveWidthRef = useRef<number>(0);
  const lastPositiveHeightRef = useRef<number>(0);
  const isContainerReady = useContainerReady(size, { minWidth: 40, minHeight: 60 });

  const textBoldShadow = '0 0 .01px black, 0 0 .01px black, 0 0 .01px black';

  const [selectedLabel, setSelectedLabel] = useState(false);
  const [labelClicked, setLabelClicked] = useState(false);

  const exportFileName = buildChartExportFileName(chartTitle, filterValues, prism);

  useEffect(() => {
    const onScroll = () => {
      const elements = document.getElementsByClassName('xLabel');

      for (let i = 0; i < elements.length; i++) {
        (elements[i] as unknown as any).style.display = 'none';
      }
    };
    // clean up code
    window.removeEventListener('scroll', onScroll);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    function handleClick(e: any) {
      const file = (e.target as Element).getAttribute('download');
      if (file) {
        const fileType = file.substr(file.length - 3, 3);
        reactGA.event({ category: 'User', action: `Downloaded ${fileType}: ${file}` });
      }
    }
    window.addEventListener('click', handleClick);

    return function cleanupListener() {
      window.removeEventListener('click', handleClick);
    };
  }, [chartRef]);

  let minValue = 0;
  let maxValue = 0;
  let isOneBar = true;

  const safeGridHeight = typeof gridHeight === 'number' && Number.isFinite(gridHeight) ? gridHeight : 4;
  const safeGridWidth = typeof gridWidth === 'number' && Number.isFinite(gridWidth) ? gridWidth : 4;

  useEffect(() => {
    if (isContainerReady && size?.width) {
      chartRef.current?.reflow();
    }
  }, [isContainerReady, size?.width, size?.height]);

  const chartOptions = useMemo(() => {
    if (!isContainerReady) {
      return undefined;
    }

    const viewportWidth = size?.width ?? 0;
    const viewportHeight = size?.height ?? 0;

    const sortedData: IStackBarChart = cloneDeep({
      axisYLabels: data?.row_nodes?.map((r: any) => r.name),
      axisXLabels: data?.col_nodes?.map((c: any) => c.name),
      matrix: data?.data,
      data: data,
      values: [],
    });

    const categoriesData: string[] = [];
    const seriesData: any[] = [];

    for (let j = 0; j < sortedData.axisXLabels?.length; ++j) {
      const seriesObj: ISeriesObj = {
        name: sortedData.axisXLabels[sortedData.axisXLabels?.length - j - 1],
        data: [],
        color: colorScheme ? colorScheme[sortedData.axisXLabels?.length - j - 1] : undefined,
      };
      for (let i = 0; i < sortedData.axisYLabels?.length; ++i) {
        if (j === 0) categoriesData.push(sortedData.axisYLabels[i]);
        const objData = data?.data?.[i]?.[sortedData.axisXLabels?.length - j - 1] as unknown as string;
        seriesObj.data.push(objData);
      }
      const series = {
        ...seriesObj,
        data: seriesObj.data.map((d: any) => (d === 0 ? d : d < 0 ? d - maxValue * 0.06 : d + maxValue * 0.06)),
      };
      seriesData.push(series);
    }

    data?.data?.forEach((d: any) => {
      if (d.length === 1) {
        if (d[0] < minValue) minValue = d[0];
        if (d[0] > maxValue) maxValue = d[0];
      } else if (d[0] > 0 && d[1] > 0) {
        isOneBar = false;
      } else if (d[0] < d[1]) {
        if (d[0] < minValue) minValue = d[0];
        if (d[1] > maxValue) maxValue = d[1];
      } else {
        if (d[1] < minValue) minValue = d[1];
        if (d[0] > maxValue) maxValue = d[0];
      }
    });
    const getSeries = (seriesData: any) => {
      const styledSeries: any = [];
      seriesData.forEach((series: any) => {
        styledSeries.push({
          ...series,
          maxWidth: '90%',
        });
      });
      return styledSeries;
    };

    if (!data || !seriesData || seriesData.length === 0) {
      return EMPTY_OPTIONS;
    }

    const widthOffset = 32;
    const tentativeWidth = viewportWidth > 0 ? viewportWidth - widthOffset : -1;
    let resolvedChartWidth = tentativeWidth > 0 ? tentativeWidth : lastPositiveWidthRef.current;
    if (!resolvedChartWidth || resolvedChartWidth <= 0) {
      resolvedChartWidth = 40;
    }
    lastPositiveWidthRef.current = resolvedChartWidth;

    const computedHeightFromGrid = (safeGridHeight / 4) * 454 + (safeGridHeight / 4 - 1) * 40;
    const tentativeHeight = viewportHeight > 0 ? viewportHeight : computedHeightFromGrid;
    let resolvedChartHeight = tentativeHeight > 0 ? tentativeHeight : lastPositiveHeightRef.current;
    if (!resolvedChartHeight || resolvedChartHeight <= 0) {
      resolvedChartHeight = 60;
    }
    lastPositiveHeightRef.current = resolvedChartHeight;

    return {
      custom: {
        component: 'HorizontalPosNegStackedBarChart',
        chartTitle,
        gridIndex,
        prism,
        gridHeight: safeGridHeight,
        gridWidth: safeGridWidth,
      },
      chart: {
        id: prism.replace(' ', '') + gridIndex,
        className: prism.replace(' ', ''),
        width: Math.max(resolvedChartWidth, 40),
        spacingRight: 20,
        marginLeft: safeGridWidth > 4 ? (safeGridWidth > 9 ? 360 : 230) : 172,
        height: Math.max(resolvedChartHeight, 60),
        type: 'bar',
        animation: false,
        style: {
          fontFamily: ['Montserrat', 'sans-serif'],
          left: '0px',
        },
      },
      exporting: {
        filename: exportFileName,
        csv: {
          columnHeaderFormatter: function (item: any) {
            if (item && item instanceof Highcharts.Axis) {
              if ((item as unknown as any).type === 'datetime') {
                return 'Date';
              }
              return 'Category';
            }
            return {
              columnTitle: item.name,
            };
          },
          dateFormat: '%m/%d/%Y',
        },
        enabled: true,
        buttons: {
          contextButton: {
            x: 14,
            y: 6,
            symbol: 'ellipsisvertical',
            menuClassName: 'highcharts-indicator-menu',
            menuItems: [
              'viewFullscreen',
              'downloadPNG',
              'downloadSVG',
              'downloadPDF',
              'separator',
              'downloadXLS',
              'downloadCSV',
            ],
          },
        },
        fallbackToExportServer: false,
      },
      navigation: {
        x: -100,
        menuItemHoverStyle: {
          background: '#EAF2FF',
        },
        menuItemStyle: {
          height: 30,
          fill: '#33485E',
          fontSize: 15,
          padding: '6px 12px',
        },
      },
      title: {
        text: chartTitle,
        align: 'left',
        x: 22,
        y: 28,
        style: {
          fontSize: '18px',
          color: 'var(--color-chart-title)',
          fontWeight: 500,
          letterSpacing: '0.5px',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        },
      },
      subtitle: {
        text: chartSubtitle || ' ',
        align: 'left',
        x: 22,
        y: 52,
      },
      credits: {
        enabled: false,
      },
      xAxis: {
        categories: categoriesData,
        zIndex: 1,
        labels: {
          formatter: function (): string {
            const obj = this as unknown as any;
            obj.value = obj.value;
            return `<div data-id='${`clickableLabel${prism.replace(' ', '')}${gridIndex}`}' id='${
              data.row_nodes[obj.pos].id
            }#${data.title}' data-name='${obj.value}' style='text-shadow:${
              data?.row_nodes?.filter((c: any) => String(selectedLabel).includes(c.id))[0]?.name === obj.value &&
              prism === gridPrism
                ? textBoldShadow
                : 'none'
            }' class="clickableLabel">${obj.value.length > (gridWidth > 4 ? (gridWidth > 9 ? 40 : 25) : 16) ? `${obj.value.substring(0, gridWidth > 4 ? (gridWidth > 9 ? 37 : 22) : 13)}...` : obj.value}</div>`;
          },
          events: {
            click: function (e: any) {
              const cellInfo = {
                source: 'value',
                topic: e.target.id.split('#')[0],
              };
              const chart = chartRef.current;
              if (chart.xAxisTooltip) {
                chart.xAxisTooltip.destroy();
                chart.xAxisTooltip = undefined;
              }

              const elements = document.getElementsByClassName('clickableLabel');

              for (let i = 0; i < elements.length; i++) {
                (elements[i] as unknown as any).style.textShadow = 'none';
              }

              setSelectedLabel(e.target.id);
              setLabelClicked(true);
              onClick(cellInfo);
            },
            mouseover: function () {
              if (labelClicked) return;
              const chartBox = document.querySelector(`.${prism.replace(' ', '')}`);
              const chartRect = chartBox && chartBox.getBoundingClientRect();
              const obj = this as unknown as any;
              const value = obj.value.split("'")[3];
              const name = obj.value.split("'")[5];
              const objBox = document.querySelector(`[id="${value}"`);
              const objRect = objBox && objBox.getBoundingClientRect();
              const objOrg = data.row_nodes.filter((l: any) => l.name === value);
              const description = objOrg[0]?.description;
              if (name.length < (gridWidth > 4 ? (gridWidth > 9 ? 51 : 26) : 17) && !description) return;
              const chart = chartRef.current;
              if (chart.xAxisTooltip !== undefined) chart.xAxisTooltip = undefined;
              const tooltipHtml = formatTooltipHtml({
                title: name,
                description,
                tooltipCfg,
                letterSpacingAdjustment: -0.75,
              });
              chart.xAxisTooltip = chart.renderer
                .label(tooltipHtml, 0, 0, 'rectangle', undefined, undefined, true)
                .css({
                  color: tooltipCfg.color,
                  width: 500,
                  opacity: 0,
                })
                .attr({
                  fill: hex2rgba(tooltipCfg.backgroundColor, tooltipCfg.opacity),
                  filter: 'drop-shadow(0px 0px 4px rgba(0, 0, 0, 0.35))',
                  'dominant-baseline': 'auto',
                  'stroke-width': tooltipCfg.borderWidth,
                  'stroke-dasharray':
                    tooltipCfg.borderStyle === 'solid' ? 'none' : tooltipCfg.borderStyle === 'dotted' ? '2,2' : '5,5',
                  'stroke-opacity': tooltipCfg.opacity,
                  padding: Number(tooltipCfg.padding.replace('px', '')),
                  r: Number(tooltipCfg.borderRadius.replace('px', '')),
                  class: 'xLabel',
                })
                .add()
                .toFront();
              chart.xAxisTooltipArrow = chart.renderer
                .label(``, 0, 0, 'rectangle')
                .css({
                  color: tooltipCfg.color,
                  width: tooltipCfg.maxWidth,
                  opacity: 0,
                })
                .attr({
                  fill: hex2rgba(tooltipCfg.backgroundColor, tooltipCfg.opacity),
                  filter: 'drop-shadow(0 2px 4px rgba(0, 0, 0, 0.2))',
                  height: 4,
                  width: 4,
                  'dominant-baseline': 'auto',
                  'stroke-width': tooltipCfg.borderWidth,
                  'stroke-dasharray':
                    tooltipCfg.borderStyle === 'solid' ? 'none' : tooltipCfg.borderStyle === 'dotted' ? '2,2' : '5,5',
                  'stroke-opacity': tooltipCfg.opacity,
                  padding: 2,
                  r: 0,
                  class: 'xLabelArrow',
                })
                .add()
                .toFront();
              chart.xAxisTooltipArrowBlocker = chart.renderer
                .label(``, 0, 0, 'rectangle')
                .css({
                  color: tooltipCfg.color,
                  width: tooltipCfg.maxWidth,
                  opacity: 0,
                })
                .attr({
                  fill: hex2rgba(tooltipCfg.backgroundColor, tooltipCfg.opacity),
                  height: 3,
                  width: 17,
                  'dominant-baseline': 'auto',
                  'stroke-width': tooltipCfg.borderWidth,
                  'stroke-dasharray':
                    tooltipCfg.borderStyle === 'solid' ? 'none' : tooltipCfg.borderStyle === 'dotted' ? '2,2' : '5,5',
                  'stroke-opacity': tooltipCfg.opacity,
                  padding: 2,
                  r: 0,
                  class: 'xLabelArrowBlocker',
                })
                .add()
                .toFront();
              const tooltipRect = chart.xAxisTooltip.getBBox();
              const tooltipBorderWidth = Number(tooltipCfg.borderWidth.replace('px', ''));
              let tooltipX = objRect ? objRect!.x + objRect!.width / 2 - chartRect!.x - tooltipRect.width / 2 : 0;
              if (tooltipX < tooltipBorderWidth) tooltipX = tooltipBorderWidth;
              if (tooltipX + tooltipRect.width > chartRect!.width)
                tooltipX = chartRect!.width - tooltipRect.width - tooltipBorderWidth;
              chart.xAxisTooltip
                .attr({
                  x: 20,
                  y: objRect ? objRect!.y - chartRect!.y - tooltipRect.height - 10 : 0,
                })
                .css({
                  opacity: 1,
                });
              chart.xAxisTooltipArrow
                .attr({
                  rotation: 45,
                  x: 20 + tooltipRect.width / 2 + 4,
                  y: objRect ? objRect!.y - chartRect!.y - tooltipRect.height + 15 : 0,
                })
                .css({
                  opacity: 1,
                });
              chart.xAxisTooltipArrowBlocker
                .attr({
                  x: 20 + tooltipRect.width / 2 - 7,
                  y: objRect ? objRect!.y - chartRect!.y - tooltipRect.height + 14 : 0,
                })
                .css({
                  opacity: 1,
                });
              const htmlObj = document.querySelector<HTMLElement>(`[data-id='${value}']`);
              if (htmlObj) htmlObj.style.textShadow = textBoldShadow;
            },
            mouseout: function () {
              const obj = this as unknown as any;
              const value = obj.value.split("'")[3];
              const name = obj.value.split("'")[5];
              const objOrg = data.row_nodes.filter((l: any) => l.name === value);
              const description = objOrg[0]?.description;
              if (name.length < (gridWidth > 4 ? (gridWidth > 9 ? 51 : 26) : 17) && !description) return;
              if (labelClicked) return setLabelClicked(false);
              const chart = chartRef.current;
              if (chart.xAxisTooltip) {
                chart.xAxisTooltip.destroy();
              }
              if (chart.xAxisTooltipArrow) {
                chart.xAxisTooltipArrow.destroy();
              }
              if (chart.xAxisTooltipArrowBlocker) {
                chart.xAxisTooltipArrowBlocker.destroy();
              }
              if (value === selectedLabel) return;
              const htmlObj = document.querySelector<HTMLElement>(`[data-id='${value}']`);
              if (htmlObj) htmlObj.style.textShadow = 'none';
            },
          },
          useHTML: false,
          style: {
            cursor: 'pointer',
            whiteSpace: 'nowrap',
            fontSize: '14px',
          },
          x: gridWidth > 4 ? (gridWidth > 9 ? -330 : -198) : -140,
          align: 'left',
          minWidth: 160,
          width: 160,
        },
      },
      yAxis: {
        endOnTick: false,
        gridZIndex: -1,
        zIndex: -1,
        min:
          Number(chartYmin) || chartYmin === 0
            ? Number(chartYmin)
            : Math.abs(maxValue) > Math.abs(minValue)
              ? -maxValue
              : minValue,
        max: Number(chartYmax) || (Math.abs(maxValue) > Math.abs(minValue) ? maxValue : -minValue),
        title: {
          text: chartYtitle,
          x: -17,
          style: {
            color: 'var(--color-chart-title)',
            fontWeight: 'normal',
            letterSpacing: '0.5px',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          },
        },
        plotLines: [
          {
            value: 0,
            width: 2,
            color: 'silver',
          },
        ],
        stackLabels: {
          enabled: false,
        },
      },
      legend: {
        id: prism.replace(' ', '') + gridIndex,
        reversed: false,
        x: 13,
        y: 8,
        align: 'left',
        verticalAlign: 'top',
        floating: false,
        backgroundColor: 'white',
        shadow: false,
      },
      tooltip: {
        ...highchartsTooltipCfg(tooltipCfg),
        useHTML: true,
        shape: 'callout',
        backgroundColor: '#fff',
        borderRadius: 5,
        borderColor: 'transparent',
        shadow: true,
        positioner: function (x: any, y: any, plot: any) {
          const { plotX, plotY } = plot;
          return { x: plotX, y: plotY + 6 };
        },
        formatter: function () {
          const { x, points } = this as unknown as any;
          return `
          <span style="color:#667686;font-weight:500;font-size:14px;line-height:1.5">${x}</span><br />
          ${
            points?.[0]?.y !== 0
              ? `<span style="color:${
                  points?.[0]?.color
                };border-radius:50%;font-size:18px;line-height:0.2">\u25CF</span> <span style="color:#4C5E72;font-weight:500;font-size:13px;line-height:1.4">${
                  points?.[0]?.series?.name
                } ${Humanize.toFixed(points?.[0]?.y, 2).replace('.00', '')}%</span><br />`
              : ''
          }
          ${
            points?.[1]?.y && points?.[1]?.y !== 0
              ? `<span style="color:${
                  points?.[1]?.color
                };border-radius:50%;font-size:18px;line-height:0.2">\u25CF</span> <span style="color:#4C5E72;font-weight:500;font-size:13px;line-height:1.4">${
                  points?.[1]?.series?.name
                } ${Humanize.toFixed(points?.[1]?.y, 2).replace('.00', '')}%</span>`
              : ''
          }
        `;
        },
      },
      plotOptions: {
        series: {
          animation: false,
          maxPointWidth: 14,
          pointWidth: 14,
          cursor: 'pointer',
          point: {
            events: {
              click: (e: any) => {
                const cellInfo = {
                  source: 'value',
                  topic: data.row_nodes[e.point.index].id,
                };
                setSelectedLabel(data.row_nodes[e.point.index].id);
                setLabelClicked(true);
                onClick(cellInfo);
              },
            },
          },
          borderRadius: {
            radius: 10,
          },
        },
        bar: {
          borderWidth: 0,
          stacking: 'normal',
          dataLabels: {
            enabled: false,
          },
        },
      },
      series: getSeries(seriesData),
    };
  }, [
    data,
    size,
    isContainerReady,
    exportFileName,
    onClick,
    chartRef,
    selectedLabel,
    colorScheme,
    tooltipCfg,
    chartTitle,
    chartSubtitle,
    chartYtitle,
    prism,
    gridIndex,
    safeGridHeight,
    safeGridWidth,
    labelClicked,
    gridPrism,
    chartYmax,
    chartYmin,
  ]);

  return (
    <Root $gridheight={safeGridHeight} id={'horizontalStackedBarChart' + gridIndex}>
      <HighchartContainer $isnegative={minValue < 0 || isOneBar ? 1 : 0}>
        {!isContainerReady && (
          <LoaderWrapper>
            <Loader />
          </LoaderWrapper>
        )}
        {isContainerReady && (
          <HighchartsReact
            containerProps={{ style: { height: '100%' } }}
            style={{ flex: 1 }}
            highcharts={Highcharts}
            constructorType={'chart'}
            options={chartOptions ?? EMPTY_OPTIONS}
            callback={(chart: any) => (chartRef.current = chart)}
          />
        )}
      </HighchartContainer>
    </Root>
  );
}
